// Página de emissão de NFCe - Venda rápida
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Trash2, ShoppingCart, CreditCard, DollarSign, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { getProdutos } from '../../../services/produtosService';
import { createNotaFiscal } from '../../../services/notasFiscaisService';
import { supabase } from '../../../lib/supabase';
import type { Produto, ItemNota as ItemNotaType, Cliente } from '../../../types';

interface ItemVenda {
  id: string;
  produto: Produto;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
}

export function NFCe() {
  const navigate = useNavigate();
  const { empresaSelecionada, contaId } = useEmpresa();
  const [itens, setItens] = useState<ItemVenda[]>([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [formaPagamento, setFormaPagamento] = useState('dinheiro');
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar produtos do banco
  useEffect(() => {
    if (!empresaSelecionada) return;

    const carregarProdutos = async () => {
      setLoading(true);
      try {
        const produtosData = await getProdutos(empresaSelecionada.id);
        setProdutos(produtosData);
      } catch (err: any) {
        console.error('[NFCe] Erro ao carregar produtos:', err);
        setError('Erro ao carregar produtos');
      } finally {
        setLoading(false);
      }
    };

    carregarProdutos();
  }, [empresaSelecionada]);
  
  const adicionarItem = () => {
    const produto = produtos.find(p => p.id === produtoSelecionado);
    if (!produto) return;
    
    const itemExistente = itens.find(i => i.produto.id === produto.id);
    if (itemExistente) {
      const novosItens = itens.map(item =>
        item.id === itemExistente.id
          ? { ...item, quantidade: item.quantidade + quantidade, valorTotal: item.valorUnitario * (item.quantidade + quantidade) }
          : item
      );
      setItens(novosItens);
    } else {
      const novoItem: ItemVenda = {
        id: Date.now().toString(),
        produto,
        quantidade,
        valorUnitario: produto.precoVenda,
        valorTotal: produto.precoVenda * quantidade,
      };
      setItens([...itens, novoItem]);
    }
    
    setProdutoSelecionado('');
    setQuantidade(1);
  };
  
  const removerItem = (id: string) => {
    setItens(itens.filter(i => i.id !== id));
  };
  
  const atualizarQuantidade = (id: string, novaQuantidade: number) => {
    if (novaQuantidade <= 0) {
      removerItem(id);
      return;
    }
    setItens(itens.map(item =>
      item.id === id
        ? { ...item, quantidade: novaQuantidade, valorTotal: item.valorUnitario * novaQuantidade }
        : item
    ));
  };
  
  const subtotal = itens.reduce((acc, item) => acc + item.valorTotal, 0);
  const desconto = 0;
  const total = subtotal - desconto;

  // Gerar número e série da nota
  const gerarNumeroSerie = () => {
    const numero = Math.floor(Date.now() / 1000).toString().slice(-6);
    const serie = '1';
    return { numero, serie };
  };

  // Buscar contaId da empresa se não estiver no contexto
  const getContaId = async (): Promise<string> => {
    if (contaId) return contaId;
    
    const { data, error } = await supabase
      .from('empresas')
      .select('conta_id')
      .eq('id', empresaSelecionada!.id)
      .single();
    
    if (error || !data?.conta_id) {
      throw new Error('Erro ao obter conta_id da empresa');
    }
    
    return data.conta_id;
  };

  // Criar cliente genérico para NFCe (venda rápida não exige cadastro)
  const criarClienteGenerico = (): Cliente => {
    return {
      id: 'consumidor-final',
      nome: 'CONSUMIDOR FINAL',
      tipo: 'F',
      cpf: '000.000.000-00',
      email: 'consumidor@final.com',
      telefone: '',
      endereco: {
        cep: '00000-000',
        logradouro: 'Não informado',
        numero: '0',
        bairro: 'Não informado',
        cidade: 'Não informado',
        estado: 'XX',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  };

  // Emitir NFCe
  const handleEmitir = async () => {
    if (!empresaSelecionada || itens.length === 0) {
      setError('Adicione pelo menos um produto');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const contaIdFinal = await getContaId();
      const { numero, serie } = gerarNumeroSerie();
      const clienteGenerico = criarClienteGenerico();

      // Converter itens para formato do banco
      const itensNota: ItemNotaType[] = itens.map(item => ({
        id: item.id,
        tipo: 'produto' as const,
        produtoId: item.produto.id,
        descricao: item.produto.descricao,
        quantidade: item.quantidade,
        unidadeMedida: item.produto.unidadeMedida,
        valorUnitario: item.valorUnitario,
        valorTotal: item.valorTotal,
        ncm: item.produto.ncm,
        cfop: undefined,
      }));

      // Criar nota fiscal como emitida
      const nota = await createNotaFiscal(
        {
          tipo: 'NFCe',
          numero,
          serie,
          emitente: empresaSelecionada,
          destinatario: clienteGenerico,
          dataEmissao: new Date().toISOString(),
          itens: itensNota,
          subtotal,
          desconto,
          total,
          status: 'emitida',
          formaPagamento,
        },
        contaIdFinal,
        empresaSelecionada.id
      );

      navigate(`/app/notas/${nota.id}`);
    } catch (err: any) {
      console.error('[NFCe] Erro ao emitir nota:', err);
      setError(err.message || 'Erro ao emitir nota');
    } finally {
      setSaving(false);
    }
  };

  // Cancelar venda
  const handleCancelar = () => {
    setItens([]);
    setProdutoSelecionado('');
    setQuantidade(1);
    setError(null);
  };

  const podeEmitir = empresaSelecionada && itens.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-600" />
            <p className="text-gray-600 dark:text-gray-400">
              Selecione uma empresa para emitir notas fiscais
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Emissão', path: '/app/emissao/nfce' }, { label: 'NFCe' }]} />
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Venda Rápida - NFCe</h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Área de Venda */}
        <div className="lg:col-span-2 space-y-6">
          {/* Adicionar Produto */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Adicionar Produto
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Produto"
                value={produtoSelecionado}
                onChange={(e) => setProdutoSelecionado(e.target.value)}
                options={[
                  { value: '', label: 'Selecione um produto' },
                  ...produtos.map(p => ({ value: p.id, label: `${p.codigo} - ${p.descricao}` })),
                ]}
              />
              <Input
                label="Quantidade"
                type="number"
                value={quantidade.toString()}
                onChange={(e) => setQuantidade(Number(e.target.value))}
                min="1"
              />
              <div className="flex items-end">
                <Button onClick={adicionarItem} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </div>
          </Card>
          
          {/* Itens da Venda */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Itens da Venda
            </h2>
            {itens.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum item adicionado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {itens.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{item.produto.descricao}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {formatCurrency(item.valorUnitario)} x {item.quantidade}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => atualizarQuantidade(item.id, item.quantidade - 1)}
                          className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                        >
                          -
                        </button>
                        <Input
                          type="number"
                          value={item.quantidade.toString()}
                          onChange={(e) => atualizarQuantidade(item.id, Number(e.target.value))}
                          className="w-20"
                          min="1"
                        />
                        <button
                          onClick={() => atualizarQuantidade(item.id, item.quantidade + 1)}
                          className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                        >
                          +
                        </button>
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 w-24 text-right">
                        {formatCurrency(item.valorTotal)}
                      </p>
                      <button
                        onClick={() => removerItem(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
        
        {/* Checkout */}
        <div className="lg:col-span-1">
          <Card className="sticky top-24">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Checkout
            </h2>
            
            {/* Forma de Pagamento */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Forma de Pagamento
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setFormaPagamento('dinheiro')}
                  className={`p-3 rounded-lg border transition-colors ${
                    formaPagamento === 'dinheiro'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <DollarSign className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Dinheiro</span>
                </button>
                <button
                  onClick={() => setFormaPagamento('cartao')}
                  className={`p-3 rounded-lg border transition-colors ${
                    formaPagamento === 'cartao'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <CreditCard className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Cartão</span>
                </button>
              </div>
            </div>
            
            {/* Totais */}
            <div className="space-y-3 mb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Desconto:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(desconto)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Total:</span>
                <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">{formatCurrency(total)}</span>
              </div>
            </div>
            
            {/* Mensagem de erro */}
            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            {/* Ações */}
            <div className="space-y-2">
              <Button
                variant="primary"
                className="w-full"
                disabled={!podeEmitir || saving}
                isLoading={saving}
                onClick={handleEmitir}
                size="lg"
              >
                Emitir NFCe
              </Button>
              <Button 
                variant="secondary" 
                className="w-full" 
                disabled={itens.length === 0}
                onClick={handleCancelar}
              >
                Cancelar Venda
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
